import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperAvatarComponent, PaperButtonComponent } from '@ricoh-paper/angular';

import type { IncomingView } from '../data/call-dock.state';

/**
 * im-call-incoming-card — somebody is calling (beads indoor_mapping_poc-0sm8.3, ticket 1.16 UI).
 *
 * Their avatar (in their colour), their name and what they want, with Accept and Decline. A ring while the viewer is
 * already in a call is the quiet case: no sound, it says so, and Accept reads "End and answer". A knock (a colleague
 * asking into the viewer's call) offers Let in and Not now. Silence stops the ring and leaves the card.
 *
 * Something you read and decide on, so a SOLID reading surface (decision 20), not glass. The accent is only on the
 * primary button. `role="alertdialog"` with the name in its label, so a screen reader announces who is calling.
 * Presentational: `im-call-dock-host` gives it the view and acts on its outputs.
 */
@Component({
  selector: 'im-call-incoming-card',
  standalone: true,
  imports: [TranslocoPipe, PaperAvatarComponent, PaperButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ph-no-capture' },
  template: `
    @let v = view();
    <section
      class="card"
      role="alertdialog"
      data-testid="emp.calls.incoming"
      [attr.data-kind]="v.kind"
      [attr.data-busy]="v.busy"
      [attr.data-call]="v.callId"
      [attr.aria-label]="headline() | transloco: { name: displayName() }"
    >
      <div class="who">
        <paper-avatar [name]="v.person.name || null" [image]="v.person.picture" [size]="48" [decorative]="true" />
        <div class="lines">
          <p class="name ph-mask" data-testid="emp.calls.incoming.name">{{ displayName() }}</p>
          <p class="what" data-testid="emp.calls.incoming.what">{{ what() | transloco }}</p>
        </div>
        @if (v.kind === 'RING' && !v.busy && !v.silenced) {
          <paper-button
            class="silence"
            shape="icon"
            size="sm"
            intent="ghost"
            icon="bell-off"
            data-testid="emp.calls.incoming.silence"
            [ariaLabel]="'flow_call.incoming.silence' | transloco"
            [title]="'flow_call.incoming.silence' | transloco"
            (click)="silence.emit()"
          />
        }
      </div>
      <div class="actions">
        <paper-button
          shape="pill"
          size="md"
          intent="destructive"
          variant="soft"
          [block]="true"
          [icon]="v.kind === 'RING' ? 'phone-off' : 'x'"
          data-testid="emp.calls.incoming.decline"
          (click)="decline.emit()"
        >
          {{ (v.kind === 'RING' ? 'flow_call.incoming.decline' : 'flow_call.incoming.not_now') | transloco }}
        </paper-button>
        <paper-button
          shape="pill"
          size="md"
          intent="primary"
          [block]="true"
          [icon]="v.kind === 'RING' ? 'phone' : 'user-plus'"
          data-testid="emp.calls.incoming.accept"
          (click)="accept.emit()"
        >
          {{ acceptKey() | transloco }}
        </paper-button>
      </div>
    </section>
  `,
  styles: `
    @use '@ricoh-paper/angular/styles/recipes/surface' as surface;

    :host { display: block; }
    .card {
      @include surface.reading-surface;
      display: grid;
      gap: 14px;
      width: min(360px, calc(100vw - 32px));
      padding: 16px;
      border-radius: var(--radius, 16px);
      border: 0;
      /* A lift, never an edge: no outlined, rounded card (ruling 21). */
      box-shadow: 0 18px 48px color-mix(in srgb, var(--text) 16%, transparent), 0 2px 6px color-mix(in srgb, var(--text) 8%, transparent);
      color: var(--text);
      font-family: var(--font-sans);
    }
    .who { display: flex; align-items: center; gap: 12px; min-width: 0; }
    .lines { flex: 1; min-width: 0; }
    .name {
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: var(--fs-display, 18px);
      font-weight: var(--fw-medium, 500);
    }
    .what { margin: 2px 0 0; color: var(--text-secondary); font-size: var(--fs-body, 13px); }
    .silence { flex: none; align-self: flex-start; }
    .actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  `,
})
export class CallIncomingCardComponent {
  readonly view = input.required<IncomingView>();
  /** "Colleague" in the viewer's language, for a caller the directory has not named yet. */
  readonly fallbackName = input('');

  readonly accept = output<void>();
  readonly decline = output<void>();
  readonly silence = output<void>();

  protected readonly displayName = computed(() => this.view().person.name || this.fallbackName());
  protected readonly headline = computed(() =>
    this.view().kind === 'KNOCK' ? 'flow_call.incoming.knock_label' : 'flow_call.incoming.ring_label',
  );
  protected readonly what = computed(() => {
    const v = this.view();
    if (v.kind === 'KNOCK') return 'flow_call.incoming.knocking';
    return v.busy ? 'flow_call.incoming.calling_busy' : 'flow_call.incoming.calling';
  });
  protected readonly acceptKey = computed(() => {
    const v = this.view();
    if (v.kind === 'KNOCK') return 'flow_call.incoming.let_in';
    return v.busy ? 'flow_call.incoming.end_and_answer' : 'flow_call.incoming.accept';
  });
}
